<template>
  <div id="devices-view">
    <v-card>
      <v-toolbar elevation="0">
        <router-link class="mr-3 text-decoration-none" icon to="/devices">
          <v-icon>mdi-arrow-left</v-icon>
        </router-link>

        <v-toolbar-title>{{ $t('DeviceView.DeviceOverview') }}</v-toolbar-title>
      </v-toolbar>

      <v-divider></v-divider>
      <div v-if="device" class="container">
        <div class="row align-items-lg-stretch">
          <div class="col-md-6 col-xs-12">
            <div class="devices-wrap">
              <div class="devices-icon">
                <svg v-if="device.isKiosk" xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="110px"
                  viewBox="0 0 24 24" width="80px" fill="#FCD707">
                  <g><rect fill="none" height="24" width="24" /></g>
                  <g>
                    <g>
                      <g><path d="M18,0H6C4.34,0,3,1.34,3,3v18c0,1.66,1.34,3,3,3h12c1.66,0,3-1.34,3-3V3C21,1.34,19.66,0,18,0z M14,22h-4v-1h4V22z M19.25,19H4.75V3h14.5V19z" /></g>
                    </g>
                  </g>
                </svg>
                <v-icon v-else-if="device.isDigitalSignageTV" size="80" color="#7E5CEF">mdi-television</v-icon>
                <v-icon v-else-if="device.isTV" size="80" color="#1948b3">mdi-television</v-icon>
                <v-icon v-else-if="device.isMobile" size="80" color="#1948b3">mdi-cellphone</v-icon>
                <svg v-else xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 24 24" height="110px"
                  viewBox="0 0 24 24" width="80px" fill="#3F51B5">
                  <g>
                    <rect fill="none" height="24" width="24" />
                  </g>
                  <g>
                    <g>
                      <g>
                        <path
                          d="M18,0H6C4.34,0,3,1.34,3,3v18c0,1.66,1.34,3,3,3h12c1.66,0,3-1.34,3-3V3C21,1.34,19.66,0,18,0z M14,22h-4v-1h4V22z M19.25,19H4.75V3h14.5V19z" />
                      </g>
                    </g>
                  </g>
                </svg>
              </div>
              <div class="devices-info">
                <ul class="devices-list">
                  <li>
                    <div class="left">
                      <b>{{ $t('DeviceView.BrandDevice') }}:</b>
                      <p>{{ device.brand }}</p>
                    </div>
                    <v-btn v-if="canUnassignDevice" class="devices-btn" outlined uppercase color="primary" @click="unassignDevice">
                      {{ $t('DeviceView.UnassignDevice') }}
                    </v-btn>
                  </li>
                  <li>
                    <div class="left">
                      <b>{{ $t('DeviceView.ModelDevice') }}:</b>
                      <p>{{ device.model }}</p>
                    </div>
                  </li>
                  <li v-if="!device.isKiosk">
                    <div class="left">
                      <b v-if="device.userType == userTypeEnum.Resident">{{ $t('GeneralTranslations.Resident') }}:</b>
                      <b v-if="device.userType == userTypeEnum.Staff">{{ $t('GeneralTranslations.Staff') }}:</b>
                      <div class="device-resident">
                        <img :src="device.imageUrl" alt="no image" />
                        <b>{{ device.userName }}</b>
                      </div>
                    </div>
                    <v-btn color="primary" text @click="routerToUser()">{{ $t('GeneralTranslations.View') }}</v-btn>
                  </li>
                  <li>
                    <div class="d-flex">
                      <b>{{ $t('DevicesList.FriendlyName') }}:</b>

                      <validation-observer ref="observer">
                          <validation-provider v-slot="{ errors }" name="Friendly Name" rules="max:50">
                            <v-text-field :disabled="!isEdit" v-model="device.friendlyName" :error-messages="errors" required counter="50"></v-text-field>
                          </validation-provider>
                      </validation-observer>

                      <div>
                        <v-btn icon color="primary" width="38" v-if="isEdit" @click="updateDevice()"
                          :loading="saving"><v-icon>mdi-check</v-icon></v-btn>
                      </div>

                      <div>
                        <v-btn icon color="primary" width="38" v-if="!isEdit" @click="isEdit = true">
                          <v-icon>mdi-pen</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Loader v-else />
    </v-card>

    <ConfirmMessageDlg ref="confirmationModal" />
  </div>
</template>

<script>
import ConfirmMessageDlg from "@/components/popup/confirm-message-dialog/ConfirmMessageDlg.vue";
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import Loader from "@/components/loader/Loader.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";

export default {
  name: "DeviceView",
  components: {
    Loader,
    ConfirmMessageDlg,
    ValidationObserver,
    ValidationProvider
  },
  data() {
    return {
      userTypeEnum: UserTypeEnum,
      saving: false,
      isEdit: false
    };
  },
  computed: {
    isDigitalSignageTv() {
      return this.$route?.query?.deviceType === "digitalSignageTV";
    },
    device() {
      if(this.isDigitalSignageTv) {
        return this.$store.state.digitalSignageModule.digitalSignageTV;
      } else {
        return this.$store.state.devicesModule.device;
      }
    },
    user() {
        return this.$store.state.authModule.user;
    },
    canUnassignDevice(){
        return hasAccess(this.user.accessLevel, AccessMatrixKeys.Devices_Device_UnassignDevice)
    },
  },
  methods: {
    routerToUser() {
      if (this.device.userType === UserTypeEnum.Resident) {
        this.$router.push("/residents/" + this.device.userId);
      }
      else if (this.device.userType === UserTypeEnum.Staff) {
        this.$router.push("/settings/users/" + this.device.userId);
      }
    },
    async updateDevice() {
      try {
        this.saving = true
        if(this.isDigitalSignageTv) {
          await this.$store.dispatch("updateDigitalSignageTV", {
            id: this.device.id,
            updates: {
              friendlyName: this.device.friendlyName
            }
          });
        } else {
          await this.$store.dispatch("updateDeviceInfo", { id: this.device.id, friendlyName: this.device.friendlyName });
        }
        this.isEdit = false
      } finally {
        this.saving = false
      }
    },
    unassignDevice() {
      this.$refs.confirmationModal
        .open(null, this.$t('DeviceView.AreYouSureYouWantToUnassignTheDevice'), false, "", null,
          this.$t('DeviceView.DeviceHasBeenUnassigned'),
          this.$t('DeviceView.AnErrorOccurredDuringTheProcess'),
          this.$t('DeviceView.DeviceIsBeingUnassignedAndWillBeLoggedOutShortly')
        )
        .then(response => {
          if (response && response.agree) {

            this.$refs.confirmationModal.load();

            if(this.isDigitalSignageTv) {
              this.$store.dispatch("unAssignDigitalSignageTV", this.device.id).then(() => {
                this.$router.push("/devices");
              }).catch(() => {
                this.$toast.open({ message: this.$t('DeviceView.UnableToUnassignDevice'), type: 'error' })
                this.$refs.confirmationModal.cancel()
              })
            } else {
              this.$store.dispatch("unAssignDevice", {
                userId: this.device.userId,
                deviceId: this.device.id,
                userType: this.userType
              }).then(() => {
                this.$router.push("/devices");
              }).catch(() => {
                this.$toast.open({ message: this.$t('DeviceView.UnableToUnassignDevice'), type: 'error' })
                this.$refs.confirmationModal.cancel()
              })
            }
          }
        });
    }
  },
  mounted() {
    if(this.isDigitalSignageTv) {
      this.$store.dispatch('getDigitalSignageTV', this.$route.params.id)
    } else {
      this.$store.dispatch('getDeviceById', this.$route.params.id);
    }
  }
};
</script>

<style lang="scss" scoped>
#devices-view .v-list-item {
  display: block;
}

.devices {
  &-btn {
    background: white;

    span {
      color: black !important;
      font-weight: 700;
    }
  }

  &-wrap {
    display: flex;
    background: #F7F7FC;
    border: 1px solid #EDEFFB;
    border-radius: 4px;
    padding: 20px;
  }

  &-list {
    list-style: none;
    padding-left: 50px !important;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 15px;
    }

    b {
      font: 800 20px/28px "Roboto";
      letter-spacing: 0.5px;
      width: 200px;
    }

    p {
      margin-bottom: 0;
      font: 400 20px/28px "Roboto";
      letter-spacing: 0.5px;
      color: rgba(0, 0, 0, 0.7);
    }

    .left {
      display: flex;
      align-items: center;
    }

    .v-btn {
      font-weight: 600;
    }
  }

  &-icon {
    width: 15%;
  }

  &-info {
    width: 85%;
  }
}

.device-resident {
  img {
    width: 42px;
    height: 42px;
    border-radius: 42px;
    margin-right: 15px;
  }
}
</style>
